import {
  UseMutationResult,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { warehouseApi } from 'src/api/warehouse';
import { translateKey } from 'src/locales/translate';
import {
  ChangeWarehouseDemandRequestStatusDto,
  CreateWarehouseDemandRequestBatchDto,
  WarehouseDemandRequestResponseDto,
} from 'woofwoof-api';
import { warehouseErrorMessage } from '../utils/errors';
import { shouldKeepOperationKey } from '../utils/operationKeyOutcome';
import {
  requireOrganizationId,
  warehouseRequestOptions,
} from '../utils/request';
import { demandScopeKey } from './warehouseDemandQueryKeys';

/** A submission of several requests and the organization of the form that built it (F-3). */
export interface CreateDemandBatchVariables {
  organizationId: string | undefined;
  dto: CreateWarehouseDemandRequestBatchDto;
}

/** A status change and the organization of the list it was made in (F-3). */
export interface DemandStatusChange {
  organizationId: string | undefined;
  id: string;
  dto: ChangeWarehouseDemandRequestStatusDto;
}

/** A free-text request linked to a catalogue Product, in the list's organization. */
export interface DemandProductLink {
  organizationId: string | undefined;
  id: string;
  productId: string;
}

/** A linked free-text request sent back to its name, in the list's organization. */
export interface DemandProductUnlink {
  organizationId: string | undefined;
  id: string;
}

export interface WarehouseDemandMutations {
  /** One request per line, in line order. */
  createBatch: UseMutationResult<
    WarehouseDemandRequestResponseDto[],
    unknown,
    CreateDemandBatchVariables
  >;
  changeStatus: UseMutationResult<
    WarehouseDemandRequestResponseDto,
    unknown,
    DemandStatusChange
  >;
  /** No confirmation: the PZ form opening next is the feedback. */
  linkProduct: UseMutationResult<
    WarehouseDemandRequestResponseDto,
    unknown,
    DemandProductLink
  >;
  unlinkProduct: UseMutationResult<
    WarehouseDemandRequestResponseDto,
    unknown,
    DemandProductUnlink
  >;
}

/**
 * Demand Request writes. Each request names the organization pinned with its
 * variables (S3), and every outcome refreshes that organization's lists: an
 * ambiguous answer may hide a saved request until its retry (D-3).
 */
export function useWarehouseDemandMutations(): WarehouseDemandMutations {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const refresh = (organizationId: string | undefined): void => {
    void queryClient.invalidateQueries(demandScopeKey(organizationId));
  };
  const confirm = (message: string): void => {
    enqueueSnackbar(message, { variant: 'success' });
  };
  const refuse = (error: unknown): void => {
    enqueueSnackbar(warehouseErrorMessage(error, translateKey), {
      variant: 'error',
    });
  };

  const createBatch = useMutation({
    mutationFn: async ({ organizationId, dto }: CreateDemandBatchVariables) =>
      (
        await warehouseApi.warehouseDemandRequestControllerCreateBatch(
          { createWarehouseDemandRequestBatchDto: dto },
          warehouseRequestOptions(requireOrganizationId(organizationId)),
        )
      ).data.data,
    onSuccess: (requests) =>
      confirm(
        translateKey('warehouse.demand.snackbar.createdBatch', {
          count: requests.length,
        }),
      ),
    // D-3: an outcome that keeps the operation key (no response, 408, 429,
    // 5xx, 422 WAREHOUSE_DISABLED) is not a failure: the form freezes the
    // batch, and its banner says why and offers "Ponów zapis". Without a
    // pinned organization nothing was sent, so that is a failure.
    onError: (error, { organizationId }) => {
      if (!organizationId || !shouldKeepOperationKey(error)) {
        refuse(error);
      }
    },
    onSettled: (_data, _error, { organizationId }) => refresh(organizationId),
  });

  const changeStatus = useMutation({
    mutationFn: async ({ organizationId, id, dto }: DemandStatusChange) =>
      (
        await warehouseApi.warehouseDemandRequestControllerChangeStatus(
          { id, changeWarehouseDemandRequestStatusDto: dto },
          warehouseRequestOptions(requireOrganizationId(organizationId)),
        )
      ).data,
    onSuccess: () =>
      confirm(translateKey('warehouse.demand.snackbar.statusChanged')),
    onError: refuse,
    onSettled: (_data, _error, { organizationId }) => refresh(organizationId),
  });

  const linkProduct = useMutation({
    mutationFn: async ({ organizationId, id, productId }: DemandProductLink) =>
      (
        await warehouseApi.warehouseDemandRequestControllerLinkProduct(
          { id, linkWarehouseDemandRequestProductDto: { productId } },
          warehouseRequestOptions(requireOrganizationId(organizationId)),
        )
      ).data,
    onError: refuse,
    onSettled: (_data, _error, { organizationId }) => refresh(organizationId),
  });

  const unlinkProduct = useMutation({
    mutationFn: async ({ organizationId, id }: DemandProductUnlink) =>
      (
        await warehouseApi.warehouseDemandRequestControllerUnlinkProduct(
          { id },
          warehouseRequestOptions(requireOrganizationId(organizationId)),
        )
      ).data,
    onSuccess: () =>
      confirm(translateKey('warehouse.demand.snackbar.unlinked')),
    onError: refuse,
    onSettled: (_data, _error, { organizationId }) => refresh(organizationId),
  });

  return { createBatch, changeStatus, linkProduct, unlinkProduct };
}
